/**
 * CHYRONYX™ — Fragrance Match Mini-Game Engine with Full Telemetry
 * 12 Cards (6 Authentic Collection Octo Perfume Pairs)
 * First-visit welcome prompt, 1-time discount award (5% for either outcome), unlimited practice replays.
 * Fully instrumented with window.chyronyxTrack for Meta Pixel, GA, and custom analytics.
 */

class ChyronyxMiniGame {
  constructor() {
    this.winCode = 'NOBLE-ACCORD5';
    this.loseCode = 'NOBLE-ACCORD5';
    this.winPercent = 5;
    this.losePercent = 5;

    this.duration = 45;
    this.maxTurns = 15;
    this.storeDomain = 'chyronyx.com';

    // Elements
    this.entryBackdrop = document.querySelector('#cyEntryBackdrop');
    this.entryCloseBtn = document.querySelector('#cyEntryCloseBtn');
    this.entryPlayBtn = document.querySelector('#cyEntryPlayBtn');
    this.entryDismissBtn = document.querySelector('#cyEntryDismissBtn');

    this.gameBackdrop = document.querySelector('#cyGameBackdrop');
    this.gameCloseBtn = document.querySelector('#cyGameCloseBtn');
    this.startBtn = document.querySelector('#cyStartBtn');
    this.retryBtn = document.querySelector('#cyRetryBtn');
    this.shopBtn = document.querySelector('#cyShopBtn');
    this.copyBtn = document.querySelector('#cyCopyBtn');
    this.toast = document.querySelector('#cyToast');
    this.cardsGrid = document.querySelector('#cyCardsGrid');
    this.memorizeBanner = document.querySelector('#cyMemorizeBanner');

    // Screens
    this.screenIntro = document.querySelector('#cyScreenIntro');
    this.screenGame = document.querySelector('#cyScreenGame');
    this.screenResult = document.querySelector('#cyScreenResult');

    // HUD
    this.timerVal = document.querySelector('#cyTimerVal');
    this.turnsVal = document.querySelector('#cyTurnsVal');
    this.pairsVal = document.querySelector('#cyPairsVal');

    // Result elements
    this.resultBadge = document.querySelector('#cyResultBadge');
    this.resultTitle = document.querySelector('#cyResultTitle');
    this.resultMsg = document.querySelector('#cyResultMsg');
    this.voucherDiscount = document.querySelector('#cyVoucherDiscount');
    this.voucherCodeEl = document.querySelector('#cyVoucherCode');
    this.voucherSub = document.querySelector('#cyVoucherSub');
    this.postgameNotice = document.querySelector('#cyPostgameNotice');

    // Practice Mode elements
    this.practiceBanner = document.querySelector('#cyPracticeBanner');
    this.discountsRow = document.querySelector('#cyDiscountsRow');

    // 6 Authentic Perfumes (12 cards total)
    this.perfumes = [
      { id: 'velvet-midnight', name: 'Velvet Midnight', notes: 'Rose & Oud' },
      { id: 'ignis', name: 'Ignis', notes: 'Tobacco & Pepper' },
      { id: 'eragon', name: 'Eragon', notes: 'Vanilla & Smoked Wood' },
      { id: 'ethereal', name: 'Ethereal', notes: 'Citrus & Mint' },
      { id: 'pulse', name: 'Pulse', notes: 'Rain & Cedarwood' },
      { id: 'isla', name: 'Isla', notes: 'Sea Salt & Amber' }
    ];

    // State
    this.state = 'idle'; // 'idle' | 'memorize' | 'playing' | 'ended'
    this.timeLeft = this.duration;
    this.turnsLeft = this.maxTurns;
    this.timerInterval = null;
    this.matchedPairs = 0;
    this.flippedCards = [];
    this.isLocked = false;
    this.currentCode = '';

    this.init();
  }

  track(eventName, params) {
    if (typeof window.chyronyxTrack === 'function') {
      window.chyronyxTrack(eventName, params);
    }
  }

  init() {
    try {
      if (this.hasClaimedDiscount()) {
        localStorage.setItem('cy_saved_code', this.loseCode);
        localStorage.setItem('cy_saved_percent', '5');
      }
    } catch (e) {}
    this.bindEvents();
    // Diwali sale prices are automatic; suppress the competing first-visit voucher prompt.
    this.checkUrlTriggers();
  }

  hasClaimedDiscount() {
    try {
      return localStorage.getItem('cy_discount_awarded') === 'true';
    } catch (e) {
      return false;
    }
  }

  getSavedCode() {
    return this.loseCode;
  }

  getSavedPercent() {
    return '5';
  }

  checkFirstVisitPrompt() {}

  checkUrlTriggers() {
    const isPlayPage = window.location.pathname.includes('/pages/play');
    const hasHash = window.location.hash === '#play-game';
    const hasParam = window.location.search.includes('game=open');

    if (isPlayPage || hasHash || hasParam) {
      setTimeout(() => {
        this.openGameModal('url_trigger');
      }, 300);
    }
  }

  bindEvents() {
    // Global data-minigame-open clicks (from marquee, header button, mobile menu)
    document.querySelectorAll('[data-minigame-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.dismissEntryPrompt();
        this.openGameModal('nav_trigger');
      });
    });

    // Entry prompt actions
    if (this.entryPlayBtn) {
      this.entryPlayBtn.addEventListener('click', () => {
        this.dismissEntryPrompt();
        this.openGameModal('entry_prompt');
      });
    }

    if (this.entryDismissBtn) {
      this.entryDismissBtn.addEventListener('click', () => {
        this.dismissEntryPrompt();
      });
    }

    if (this.entryCloseBtn) {
      this.entryCloseBtn.addEventListener('click', () => {
        this.dismissEntryPrompt();
      });
    }

    if (this.entryBackdrop) {
      this.entryBackdrop.addEventListener('click', (e) => {
        if (e.target === this.entryBackdrop) this.dismissEntryPrompt();
      });
    }

    // Game modal controls
    if (this.gameCloseBtn) {
      this.gameCloseBtn.addEventListener('click', () => this.closeGameModal());
    }

    if (this.gameBackdrop) {
      this.gameBackdrop.addEventListener('click', (e) => {
        if (e.target === this.gameBackdrop) this.closeGameModal();
      });
    }

    if (this.startBtn) {
      this.startBtn.addEventListener('click', () => this.startCountdown());
    }

    if (this.retryBtn) {
      this.retryBtn.addEventListener('click', () => this.startCountdown());
    }

    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', () => this.copyVoucherCode());
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.gameBackdrop?.classList.contains('is-open')) this.closeGameModal();
        if (this.entryBackdrop?.classList.contains('is-open')) this.dismissEntryPrompt();
      }
    });
  }

  openEntryPrompt() {
    if (!this.entryBackdrop) return;
    this.entryBackdrop.classList.add('is-open');
    this.track('MiniGame_PromptShown', {
      page: window.location.pathname
    });
  }

  dismissEntryPrompt() {
    if (!this.entryBackdrop) return;
    this.entryBackdrop.classList.remove('is-open');
    try {
      localStorage.setItem('cy_entry_seen', 'true');
    } catch (e) {}
    this.track('MiniGame_PromptDismissed', {});
  }

  openGameModal(source = 'button') {
    if (!this.gameBackdrop) return;
    this.gameBackdrop.classList.add('is-open');
    this.showScreen('intro');
    this.updateIntroScreenForMode();

    this.track('MiniGame_Open', {
      source: source,
      is_practice: this.hasClaimedDiscount()
    });
  }

  closeGameModal() {
    if (!this.gameBackdrop) return;
    this.gameBackdrop.classList.remove('is-open');
    if (this.timerInterval) clearInterval(this.timerInterval);
    this.state = 'idle';

    this.track('MiniGame_ModalClose', {
      matched_pairs: this.matchedPairs
    });
  }

  updateIntroScreenForMode() {
    const isPractice = this.hasClaimedDiscount();
    const savedCode = this.getSavedCode();
    const savedPercent = this.getSavedPercent();

    if (isPractice) {
      if (this.practiceBanner) {
        this.practiceBanner.style.display = 'block';
        this.practiceBanner.innerHTML = `<strong>Practice Mode Active</strong> • You already unlocked your 1-time <strong>${savedPercent}% OFF</strong> code (<strong>${savedCode}</strong>). Feel free to play for mastery!`;
      }
      if (this.discountsRow) {
        this.discountsRow.style.opacity = '0.65';
      }
      if (this.startBtn) {
        this.startBtn.querySelector('span:first-child').textContent = 'Play Practice Match';
      }
    } else {
      if (this.practiceBanner) {
        this.practiceBanner.style.display = 'none';
      }
      if (this.discountsRow) {
        this.discountsRow.style.opacity = '1';
      }
      if (this.startBtn) {
        this.startBtn.querySelector('span:first-child').textContent = 'Start Privilege Game';
      }
    }
  }

  showScreen(screenName) {
    if (!this.screenIntro || !this.screenGame || !this.screenResult) return;
    this.screenIntro.classList.remove('is-active');
    this.screenGame.classList.remove('is-active');
    this.screenResult.classList.remove('is-active');

    if (screenName === 'intro') this.screenIntro.classList.add('is-active');
    if (screenName === 'game') this.screenGame.classList.add('is-active');
    if (screenName === 'result') this.screenResult.classList.add('is-active');
  }

  buildDeck() {
    // 6 perfumes x 2 = 12 cards
    const deck = [];
    this.perfumes.forEach(p => {
      deck.push({ ...p, key: `${p.id}-1` });
      deck.push({ ...p, key: `${p.id}-2` });
    });

    // Fisher-Yates shuffle
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }

    return deck;
  }

  renderCards() {
    if (!this.cardsGrid) return;
    this.cardsGrid.innerHTML = '';

    const deck = this.buildDeck();
    deck.forEach(cardData => {
      const card = document.createElement('div');
      card.className = 'cy-card';
      card.dataset.id = cardData.id;

      card.innerHTML = `
        <div class="cy-card-face cy-card-back">
          <div class="cy-card-emblem">♦</div>
          <div class="cy-card-label">CHYRONYX</div>
        </div>
        <div class="cy-card-face cy-card-front">
          <div class="cy-perfume-name">${cardData.name}</div>
          <div class="cy-perfume-notes">${cardData.notes}</div>
        </div>
      `;

      card.addEventListener('click', () => this.handleCardClick(card));
      this.cardsGrid.appendChild(card);
    });
  }

  startCountdown() {
    const isPractice = this.hasClaimedDiscount();
    this.track('MiniGame_Start', {
      mode: isPractice ? 'practice' : 'privilege',
      duration: this.duration,
      max_turns: this.maxTurns
    });

    this.state = 'memorize';
    this.timeLeft = this.duration;
    this.turnsLeft = this.maxTurns;
    this.matchedPairs = 0;
    this.flippedCards = [];
    this.isLocked = true;

    this.renderCards();
    this.showScreen('game');

    if (this.timerVal) this.timerVal.textContent = `${this.timeLeft}s`;
    if (this.turnsVal) {
      this.turnsVal.textContent = this.turnsLeft;
      this.turnsVal.classList.remove('warning');
    }
    if (this.pairsVal) this.pairsVal.textContent = `0 / 6`;

    // Reveal cards for 3 seconds for memorization
    const allCards = this.cardsGrid.querySelectorAll('.cy-card');
    allCards.forEach(c => c.classList.add('is-flipped'));

    if (this.memorizeBanner) {
      this.memorizeBanner.textContent = 'Memorize the cards... 7s';
      this.memorizeBanner.classList.add('is-active');
    }

    let peekCountdown = 7;
    const peekTimer = setInterval(() => {
      peekCountdown -= 1;
      if (peekCountdown > 0) {
        if (this.memorizeBanner) this.memorizeBanner.textContent = `Memorize the cards... ${peekCountdown}s`;
      } else {
        clearInterval(peekTimer);
        allCards.forEach(c => c.classList.remove('is-flipped'));
        if (this.memorizeBanner) this.memorizeBanner.classList.remove('is-active');
        this.beginPlay();
      }
    }, 1000);
  }

  beginPlay() {
    this.state = 'playing';
    this.isLocked = false;

    if (this.timerInterval) clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => {
      this.timeLeft -= 1;
      if (this.timerVal) this.timerVal.textContent = `${this.timeLeft}s`;

      if (this.timeLeft <= 0) {
        clearInterval(this.timerInterval);
        this.endGame(false, 'Time expired');
      }
    }, 1000);
  }

  handleCardClick(card) {
    if (this.state !== 'playing' || this.isLocked) return;
    if (card.classList.contains('is-flipped') || card.classList.contains('is-matched')) return;

    card.classList.add('is-flipped');
    this.flippedCards.push(card);

    const perfumeName = card.querySelector('.cy-perfume-name')?.textContent || card.dataset.id;
    this.track('MiniGame_CardFlip', {
      card_id: card.dataset.id,
      perfume_name: perfumeName,
      flips_in_turn: this.flippedCards.length
    });

    if (this.flippedCards.length === 2) {
      this.checkMatch();
    }
  }

  checkMatch() {
    this.isLocked = true;
    const [card1, card2] = this.flippedCards;
    const isMatch = card1.dataset.id === card2.dataset.id;
    const perfumeName = card1.querySelector('.cy-perfume-name')?.textContent || card1.dataset.id;

    if (isMatch) {
      this.matchedPairs += 1;
      this.track('MiniGame_MatchFound', {
        perfume_id: card1.dataset.id,
        perfume_name: perfumeName,
        matched_pairs: this.matchedPairs,
        turns_left: this.turnsLeft,
        time_left: this.timeLeft
      });

      setTimeout(() => {
        card1.classList.add('is-matched');
        card2.classList.add('is-matched');
        if (this.pairsVal) this.pairsVal.textContent = `${this.matchedPairs} / 6`;
        this.flippedCards = [];
        this.isLocked = false;

        if (this.matchedPairs === 6) {
          clearInterval(this.timerInterval);
          setTimeout(() => this.endGame(true, 'Matched all pairs'), 400);
        }
      }, 350);
    } else {
      this.turnsLeft -= 1;
      this.track('MiniGame_TurnMistake', {
        turns_left: this.turnsLeft,
        card1: card1.dataset.id,
        card2: card2.dataset.id
      });

      if (this.turnsVal) {
        this.turnsVal.textContent = this.turnsLeft;
        if (this.turnsLeft <= 2) {
          this.turnsVal.classList.add('warning');
        }
      }

      setTimeout(() => {
        card1.classList.remove('is-flipped');
        card2.classList.remove('is-flipped');
        this.flippedCards = [];
        this.isLocked = false;

        if (this.turnsLeft <= 0 && this.matchedPairs < 6) {
          clearInterval(this.timerInterval);
          this.endGame(false, 'Out of turns');
        }
      }, 1200);
    }
  }

  endGame(isWin, reason = '') {
    this.state = 'ended';
    if (this.timerInterval) clearInterval(this.timerInterval);

    const isPracticeReplay = this.hasClaimedDiscount();
    let displayCode = '';
    let displayPercent = '';
    const timeTaken = Math.max(1, this.duration - this.timeLeft);

    if (!isPracticeReplay) {
      // First official play: award the discount and lock it
      displayCode = isWin ? this.winCode : this.loseCode;
      displayPercent = isWin ? this.winPercent : this.losePercent;
      this.currentCode = displayCode;

      try {
        localStorage.setItem('cy_discount_awarded', 'true');
        localStorage.setItem('cy_saved_code', displayCode);
        localStorage.setItem('cy_saved_percent', displayPercent.toString());
        localStorage.setItem('cy_entry_seen', 'true');
      } catch (e) {}

      if (isWin) {
        this.resultBadge.textContent = 'Victory Privilege';
        this.resultTitle.textContent = 'You Won 5% Off!';
        this.resultMsg.textContent = `Masterful memory! You solved all 6 pairs in ${timeTaken}s with ${this.turnsLeft} turns remaining. Your 5% discount code is ready below.`;
        this.voucherDiscount.textContent = '5% OFF';
      } else {
        this.resultBadge.textContent = 'Guaranteed Privilege';
        this.resultTitle.textContent = 'You Get 5% Off!';
        this.resultMsg.textContent = `Good game! You matched ${this.matchedPairs} of 6 pairs. To welcome you to CHYRONYX, enjoy 5% off your order, including the current sale price.`;
        this.voucherDiscount.textContent = '5% OFF';
      }

      if (this.postgameNotice) {
        this.postgameNotice.innerHTML = `<strong>Privilege Awarded:</strong> Your single-use discount code is saved. You can replay Fragrance Match anytime from the top bar or menu bar for practice!`;
      }
    } else {
      // Practice Mode Replay: celebrate performance, retain original code, no new codes
      displayCode = this.getSavedCode();
      displayPercent = this.getSavedPercent();
      this.currentCode = displayCode;

      this.resultBadge.textContent = isWin ? 'Practice Victory' : 'Practice Complete';
      this.resultTitle.textContent = isWin ? 'Outstanding Memory!' : 'Practice Round Finished!';
      
      this.resultMsg.textContent = isWin 
        ? `You matched all 6 pairs in ${timeTaken}s with ${this.turnsLeft} turns left! Excellent training.`
        : `You completed the practice round with ${this.matchedPairs} of 6 pairs matched.`;

      this.voucherDiscount.textContent = `${displayPercent}% OFF`;

      if (this.postgameNotice) {
        this.postgameNotice.innerHTML = `<strong>Practice Mode:</strong> Discounts are single-use per customer and awarded once. Your saved code <strong>${displayCode}</strong> is ready for checkout!`;
      }
    }

    if (this.voucherCodeEl) {
      this.voucherCodeEl.textContent = displayCode;
    }

    if (this.voucherSub) {
      this.voucherSub.textContent = `5% Off • Includes Current Sale Prices`;
    }

    if (this.shopBtn) {
      this.shopBtn.textContent = 'Shop Collection ⟶';
      this.shopBtn.onclick = () => {
        this.track('MiniGame_ShopCTA', {
          code: displayCode,
          percent: displayPercent,
          destination: '/collections/all'
        });
        window.location.href = `https://${this.storeDomain}/discount/${displayCode}?redirect=/collections/all`;
      };
    }

    if (this.retryBtn) {
      this.retryBtn.textContent = 'Play Practice Match';
    }

    // Comprehensive Game End Telemetry
    this.track('MiniGame_Completed', {
      result: isWin ? 'win' : 'loss',
      mode: isPracticeReplay ? 'practice' : 'privilege',
      pairs_matched: this.matchedPairs,
      turns_left: this.turnsLeft,
      time_taken_seconds: timeTaken,
      reason: reason,
      discount_code: displayCode,
      discount_percent: displayPercent
    });

    this.showScreen('result');
  }

  copyVoucherCode() {
    if (!this.currentCode) return;
    this.track('MiniGame_DiscountCopied', {
      code: this.currentCode,
      percent: this.hasClaimedDiscount() ? this.getSavedPercent() : (this.matchedPairs === 6 ? this.winPercent : this.losePercent)
    });

    navigator.clipboard.writeText(this.currentCode).then(() => {
      this.showToast('Discount Code Copied!');
    }).catch(() => {
      this.showToast(`Code: ${this.currentCode}`);
    });
  }

  showToast(msg) {
    if (!this.toast) return;
    this.toast.textContent = msg;
    this.toast.classList.add('is-visible');
    setTimeout(() => {
      this.toast.classList.remove('is-visible');
    }, 2200);
  }
}

// Auto-initialize when DOM is ready
if (typeof window !== 'undefined') {
  window.ChyronyxMiniGame = ChyronyxMiniGame;
  document.addEventListener('DOMContentLoaded', () => {
    window.chyronyxGameInstance = new ChyronyxMiniGame();
  });
}
